/**
 * MapLibre style builder + validator.
 * Uses the official @maplibre/maplibre-gl-style-spec package to guarantee
 * that every generated style document is spec-compliant before it reaches
 * the map.
 */

import { validateStyleMin } from '@maplibre/maplibre-gl-style-spec';

const RUSSIA_BOUNDS = [19.0, 41.0, 190.0, 82.0];

export const vectorStyleUrls = {
  liberty:  'https://tiles.openfreemap.org/styles/liberty',
  positron: 'https://tiles.openfreemap.org/styles/positron',
  dark:     'https://tiles.openfreemap.org/styles/dark'
};

export const rasterConfig = {
  '2gis':       { tiles: [window.location.origin + '/tiles/proxy/2gis/{z}/{x}/{y}'],       maxzoom: 18, attribution: '© 2GIS' },
  'yandex':     { tiles: [window.location.origin + '/tiles/proxy/yandex/{z}/{x}/{y}'],     maxzoom: 19, attribution: '© Yandex', bounds: RUSSIA_BOUNDS },
  'google':     { tiles: [window.location.origin + '/tiles/proxy/google/{z}/{x}/{y}'],     maxzoom: 21, attribution: '© Google' },
  'cartodb':    { tiles: [window.location.origin + '/tiles/proxy/cartodb/{z}/{x}/{y}.png'], maxzoom: 19, attribution: '© CartoDB' },
  'osm':        { tiles: [window.location.origin + '/tiles/proxy/osm/{z}/{x}/{y}.png'],     maxzoom: 19, attribution: '© OSM' },
  'yandex-sat': { tiles: [window.location.origin + '/tiles/proxy/yandex-sat/{z}/{x}/{y}'], maxzoom: 19, attribution: '© Yandex', bounds: RUSSIA_BOUNDS },
  'google-sat': { tiles: [window.location.origin + '/tiles/proxy/google-sat/{z}/{x}/{y}'], maxzoom: 21, attribution: '© Google' },
  'satellite':  { tiles: [window.location.origin + '/tiles/proxy/esri/{z}/{y}/{x}'],       maxzoom: 19, attribution: '© ESRI' },
  'nspd':       { tiles: [window.location.origin + '/nspd/wmts/235/{z}/{x}/{y}.png'],       maxzoom: 18, attribution: '© НСПД', bounds: RUSSIA_BOUNDS },
  'digimap':    { tiles: [window.location.origin + '/tiles/proxy/digimap/{z}/{x}/{y}.png'], maxzoom: 19, attribution: '© Digimap' },
  'waze':       { tiles: [window.location.origin + '/tiles/proxy/waze/{z}/{x}/{y}.png'],    maxzoom: 19, attribution: '© Waze' },
  'bing':       { tiles: [window.location.origin + '/tiles/proxy/bing/{z}/{x}/{y}'],        maxzoom: 19, attribution: '© Bing' },
  'bing-sat':   { tiles: [window.location.origin + '/tiles/proxy/bing-sat/{z}/{x}/{y}'],    maxzoom: 19, attribution: '© Bing' }
};

export const overlaysConfig = {
  cadastre: { tiles: [window.location.origin + '/nspd/wms/36048?BBOX={bbox-epsg-3857}&WIDTH=256&HEIGHT=256'], opacity: 0.30, minzoom: 4, maxzoom: 19 },
  'nspd-zu':  { tiles: [window.location.origin + '/nspd/wms/36048?BBOX={bbox-epsg-3857}&WIDTH=256&HEIGHT=256'], opacity: 0.70, minzoom: 4, maxzoom: 19 },
  'nspd-oks': { tiles: [window.location.origin + '/nspd/wms/36049?BBOX={bbox-epsg-3857}&WIDTH=256&HEIGHT=256'], opacity: 0.70, minzoom: 4, maxzoom: 19 },
  // Map.ru cadastral layers (proxied and cached for 7 days)
  // Use 512 px tiles — map.ru renders them sharper than 256 px.
  'mapru-zu':  { tiles: [window.location.origin + '/mapru/wms/36048?BBOX={bbox-epsg-3857}&WIDTH=512&HEIGHT=512'], opacity: 0.70, minzoom: 4, maxzoom: 19, tileSize: 512 },
  'mapru-oks': { tiles: [window.location.origin + '/mapru/wms/36049?BBOX={bbox-epsg-3857}&WIDTH=512&HEIGHT=512'], opacity: 0.70, minzoom: 4, maxzoom: 19, tileSize: 512 },
  'mapru-ons': { tiles: [window.location.origin + '/mapru/wms/36329?BBOX={bbox-epsg-3857}&WIDTH=512&HEIGHT=512'], opacity: 0.70, minzoom: 4, maxzoom: 19, tileSize: 512 }
};

function makeRasterSource(cfg) {
  const source = {
    type: 'raster',
    tiles: cfg.tiles,
    tileSize: cfg.tileSize || 256,
    maxzoom: cfg.maxzoom,
    attribution: cfg.attribution || ''
  };
  if (cfg.bounds) source.bounds = cfg.bounds;
  if (cfg.minzoom !== undefined) source.minzoom = cfg.minzoom;
  return source;
}

function makeRasterLayer(id, visible) {
  return {
    id,
    type: 'raster',
    source: id,
    layout: { visibility: visible ? 'visible' : 'none' },
    paint: {
      'raster-fade-duration': 0,
      'raster-resampling': 'nearest'
    }
  };
}

/**
 * Build a complete raster base style. All base sources/layers are included;
 * only the requested active layer is visible.
 */
export function buildRasterStyle(activeKey) {
  const sources = {};
  const layers = [];

  Object.entries(rasterConfig).forEach(([key, cfg]) => {
    const id = 'base-' + key;
    sources[id] = makeRasterSource(cfg);
    layers.push(makeRasterLayer(id, key === activeKey));
  });

  const style = {
    version: 8,
    glyphs: window.location.origin + '/tiles/proxy/fonts/{fontstack}/{range}.pbf',
    sources,
    layers
  };

  const errors = validateStyleMin(style);
  if (errors && errors.length) {
    console.warn('[Style Builder] validation warnings:', errors);
  }
  return style;
}

/**
 * Build a style fragment for overlay raster sources/layers (all hidden).
 */
export function buildOverlayStyle() {
  const sources = {};
  const layers = [];

  Object.entries(overlaysConfig).forEach(([key, cfg]) => {
    const id = 'overlay-' + key;
    sources[id] = makeRasterSource(cfg);
    layers.push({
      id,
      type: 'raster',
      source: id,
      layout: { visibility: 'none' },
      paint: {
        'raster-opacity': cfg.opacity,
        'raster-fade-duration': 0,
        'raster-resampling': 'nearest'
      }
    });
  });

  return { sources, layers };
}

/**
 * Validate an arbitrary style object (e.g. a loaded vector style).
 * Returns an array of validation messages; empty means valid.
 */
export function validateStyle(style) {
  return validateStyleMin(style) || [];
}
